// Network layer: POST /chat (SSE over fetch streaming) and POST /escalate.
//
// We use fetch + ReadableStream rather than EventSource because the chat request is
// a POST with a JSON body (sessionId + message + history) — EventSource is GET-only.

import type { ChatMessage, ChatStreamHandlers, SignupResult } from "./types";

export interface ChatRequest {
  sessionId: string;
  message: string;
  history: ChatMessage[];
  /** Storefront page the customer is chatting from (location.href). */
  pageUrl?: string;
}

export interface EscalatePayload {
  sessionId: string;
  email: string;
  name?: string;
  message: string;
  transcript: ChatMessage[];
}

/** POST /chat and parse the text/event-stream response, dispatching to handlers. */
export async function streamChat(
  apiBase: string,
  body: ChatRequest,
  handlers: ChatStreamHandlers,
  signal?: AbortSignal,
): Promise<void> {
  let res: Response;
  try {
    res = await fetch(apiBase + "/chat", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
      body: JSON.stringify(body),
      signal,
    });
  } catch (err) {
    handlers.onError(toError(err));
    return;
  }

  if (!res.ok || !res.body) {
    handlers.onError(new Error("Chat request failed (" + res.status + ")"));
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE frames are separated by a blank line.
      let sep: number;
      while ((sep = buffer.indexOf("\n\n")) !== -1) {
        dispatchFrame(buffer.slice(0, sep), handlers);
        buffer = buffer.slice(sep + 2);
      }
    }
    buffer += decoder.decode();
    if (buffer.trim()) dispatchFrame(buffer, handlers);
  } catch (err) {
    if (toError(err).name === "AbortError") return;
    handlers.onError(toError(err));
  }
}

function dispatchFrame(frame: string, handlers: ChatStreamHandlers): void {
  const lines = frame.replace(/\r/g, "").split("\n");
  let event = "message";
  const dataParts: string[] = [];

  for (const line of lines) {
    if (!line || line.charAt(0) === ":") continue; // blank or comment/heartbeat
    if (line.indexOf("event:") === 0) {
      event = line.slice(6).trim();
    } else if (line.indexOf("data:") === 0) {
      dataParts.push(line.slice(5).replace(/^ /, ""));
    }
  }
  if (dataParts.length === 0) return;

  let data: any;
  try {
    data = JSON.parse(dataParts.join("\n"));
  } catch {
    return; // ignore malformed frame
  }

  switch (event) {
    case "token":
      if (data && typeof data.text === "string") handlers.onToken(data.text);
      break;
    case "sources":
      if (data && Array.isArray(data.sources)) handlers.onSources(data.sources);
      break;
    case "products":
      if (data && Array.isArray(data.products)) handlers.onProducts(data.products);
      break;
    case "escalate":
      if (data) handlers.onEscalate(data);
      break;
    case "signup":
      handlers.onSignup();
      break;
    case "agent_offer":
      handlers.onAgentOffer(
        data && typeof data.label === "string" ? data.label : "Connect me with a live agent",
      );
      break;
    case "done":
      handlers.onDone({
        inHours: !!(data && data.inHours),
        nextOpenText: data && typeof data.nextOpenText === "string" ? data.nextOpenText : null,
      });
      break;
  }
}

/** POST /escalate. Returns true on { ok: true }. */
export async function postEscalate(apiBase: string, payload: EscalatePayload): Promise<boolean> {
  try {
    const res = await fetch(apiBase + "/escalate", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) return false;
    const json = await res.json().catch(() => null);
    return !!(json && json.ok === true);
  } catch {
    return false;
  }
}

export interface SignupPayload {
  sessionId: string;
  firstName: string;
  lastName: string;
  email: string;
  company?: string;
  phone?: string;
}

/** POST /signup — create a customer account from the in-chat form. Never throws;
 *  network/server failures come back as { ok: false }. */
export async function postSignup(apiBase: string, payload: SignupPayload): Promise<SignupResult> {
  try {
    const res = await fetch(apiBase + "/signup", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const json = (await res.json().catch(() => null)) as SignupResult | null;
    if (!json || json.ok !== true) return { ok: false };
    return json;
  } catch {
    return { ok: false };
  }
}

/**
 * Tell the server where the customer actually ended up when our own console did
 * not take them. Without this the conversation sits in the CSR inbox at WAITING
 * forever, looking like an unanswered chat when it was answered in Smartsupp or
 * captured by the lead form. Best-effort: never throws, never blocks the UI.
 */
export function reportRouted(
  apiBase: string,
  sessionId: string,
  route: "smartsupp" | "lead_form" | "off_hours_form",
): void {
  try {
    const body = JSON.stringify({ sessionId, route });
    // keepalive so the report still lands if the page is navigating away.
    void fetch(apiBase + "/handoff/routed", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    /* reporting must never break a handoff */
  }
}

/** Tell the server which page the customer is on. Drives the console's visitor
 *  list and the proactive-message rules. Fire and forget. */
export function pingVisitor(apiBase: string, sessionId: string, page: string, title: string): void {
  try {
    void fetch(apiBase + "/visitor/ping", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sessionId, page: page.slice(0, 500), title: title.slice(0, 200) }),
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    /* presence is a nicety, never a blocker */
  }
}

/** Customer is typing — mirrors the indicator the agent already sends us. */
export function sendCustomerTyping(apiBase: string, sessionId: string): void {
  try {
    void fetch(apiBase + "/visitor/typing", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sessionId }),
    }).catch(() => undefined);
  } catch {
    /* ignore */
  }
}

/** Send how the chat went. */
export async function rateChat(
  apiBase: string,
  sessionId: string,
  rating: number,
  comment?: string,
): Promise<boolean> {
  try {
    const res = await fetch(apiBase + "/visitor/rate", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sessionId, rating, comment }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export interface UploadedFile {
  id: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  storageKey: string;
}

/** Upload one file. Base64 over JSON — the cap is 10MB, so the ~33% overhead is
 *  a few megabytes and it avoids a multipart parser on a public endpoint. */
export async function uploadFile(
  apiBase: string,
  sessionId: string,
  file: File,
): Promise<{ ok: true; attachment: UploadedFile } | { ok: false; error: string }> {
  try {
    const dataBase64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error("read failed"));
      reader.onload = () => {
        const result = String(reader.result || "");
        const comma = result.indexOf(",");
        resolve(comma === -1 ? result : result.slice(comma + 1));
      };
      reader.readAsDataURL(file);
    });
    const res = await fetch(apiBase + "/visitor/upload", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        sessionId,
        filename: file.name,
        mimeType: file.type || "application/octet-stream",
        dataBase64,
      }),
    });
    const json = (await res.json().catch(() => null)) as
      | { attachment?: UploadedFile; error?: string }
      | null;
    if (!res.ok || !json?.attachment) {
      return { ok: false, error: json?.error || "That file could not be sent." };
    }
    return { ok: true, attachment: json.attachment };
  } catch {
    return { ok: false, error: "That file could not be sent." };
  }
}

/** GET /config → is the storefront bot switched on? Fails closed to false (the
 *  storefront then keeps Smartsupp's own chat) if the probe can't be reached. */
export async function fetchBotEnabled(apiBase: string): Promise<boolean> {
  try {
    const res = await fetch(apiBase + "/config", { cache: "no-store" });
    if (!res.ok) return false;
    const json = await res.json().catch(() => null);
    return !!(json && json.botEnabled === true);
  } catch {
    return false;
  }
}

/** Fire-and-forget beacon: record a manual live-agent handoff so it appears in the
 *  monitoring log. The "Talk to a person" path opens Smartsupp client-side and never
 *  hits /chat, so without this the handoff is invisible on our side. Uses keepalive
 *  fetch (survives the page/UX transition), NOT sendBeacon: the widget runs cross-origin
 *  (storefront → chat.directtextilestore.com) and sendBeacon with an application/json
 *  Blob needs a CORS preflight that beacons can't perform — the browser queues it
 *  (sendBeacon returns true) and then silently drops it, which is why /handoff rows
 *  vanished from the server log between 2026-07-08 and 2026-08-06. */
export function postHandoff(
  apiBase: string,
  payload: {
    sessionId: string;
    reason: string;
    inHours: boolean;
    lastUserMessage?: string;
    pageUrl?: string;
    /** Pre-handoff contact form (2026-08-12) — the server records it on the
     *  conversation before ringing, so the CSR who claims sees who it is. */
    contact?: { name: string; email: string; orderRef?: string };
    /** 'bot' = completing a bot-initiated escalation (already logged via /chat,
     *  so the server skips its log line). Manual button sends 'manual'. */
    source?: "manual" | "bot";
  },
): void {
  const url = apiBase + "/handoff";
  try {
    const body = JSON.stringify(payload);
    void fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* best-effort; never block the handoff */
  }
}

/** Read-receipt ack: tell the server an agent message rendered (`delivered`)
 *  or rendered with the panel open (`read`). Fire-and-forget keepalive fetch,
 *  same CORS reasoning as postHandoff. */
export function postAck(
  apiBase: string,
  payload: { sessionId: string; messageId: string; kind: "delivered" | "read" },
): void {
  try {
    void fetch(apiBase + "/stream/ack", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* best-effort */
  }
}

/** GET /presence → can the customer actually reach a person right now?
 *
 *  Replaces trusting the business-hours clock alone. A schedule says "open at
 *  9am"; it cannot say whether anyone logged into the console. On 2026-08-06 a
 *  real lead was told live chat was unreachable during posted hours, which is
 *  exactly the gap this closes. Fails safe: on any error we report no agent, so
 *  the customer gets the leave-a-message form rather than a promise we can't keep. */
export async function fetchPresence(apiBase: string): Promise<{
  agentAvailable: boolean;
  inHours: boolean;
  nextOpenText: string | null;
  smartsuppFallback: boolean;
}> {
  try {
    const res = await fetch(apiBase + '/presence', { cache: 'no-store' });
    if (!res.ok) throw new Error(String(res.status));
    const json = (await res.json()) as Partial<{
      agentAvailable: boolean;
      inHours: boolean;
      nextOpenText: string | null;
      smartsuppFallback: boolean;
    }>;
    return {
      agentAvailable: json.agentAvailable === true,
      inHours: json.inHours === true,
      nextOpenText: typeof json.nextOpenText === 'string' ? json.nextOpenText : null,
      smartsuppFallback: json.smartsuppFallback !== false,
    };
  } catch {
    return { agentAvailable: false, inHours: false, nextOpenText: null, smartsuppFallback: true };
  }
}

export interface CustomerStreamHandlers {
  onAgentMessage: (m: {
    id: string;
    body: string;
    agentName: string;
    ts: string;
    attachment?: { filename: string; mimeType: string; sizeBytes: number; storageKey: string };
  }) => void;
  onAgentJoined: (agentName: string) => void;
  onAgentLeft: (agentName: string, handedBackToBot: boolean) => void;
  onAgentTyping: () => void;
  onAutoMessage: (text: string) => void;
  onRatingRequest: () => void;
  onMessageDeleted: (messageId: string) => void;
  onNoAgent: () => void;
  onClosed: () => void;
  /** A CSR opened the thread — the customer's messages were seen. */
  onMessagesRead: () => void;
}

/** Long-lived SSE channel that lets an agent's reply reach a browser which is
 *  not currently asking us anything. EventSource reconnects on its own, so the
 *  only job here is wiring listeners and handing back a closer. */
/** Narrow an untrusted SSE payload to the attachment shape, or nothing. */
function readAttachment(raw: unknown):
  | { filename: string; mimeType: string; sizeBytes: number; storageKey: string }
  | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const a = raw as Record<string, unknown>;
  if (typeof a.storageKey !== "string" || typeof a.filename !== "string") return undefined;
  return {
    filename: a.filename,
    mimeType: typeof a.mimeType === "string" ? a.mimeType : "application/octet-stream",
    sizeBytes: typeof a.sizeBytes === "number" ? a.sizeBytes : 0,
    storageKey: a.storageKey,
  };
}

export function openCustomerStream(
  apiBase: string,
  sessionId: string,
  h: CustomerStreamHandlers,
): () => void {
  if (typeof EventSource === 'undefined') return () => {};
  let source: EventSource;
  try {
    // page/title ride along so the console's visitor list is populated the
    // moment someone lands, not only once they navigate.
    const q = new URLSearchParams({ sessionId });
    try {
      q.set('page', location.href.slice(0, 500));
      if (document.title) q.set('title', document.title.slice(0, 200));
    } catch {
      /* non-browser context */
    }
    source = new EventSource(`${apiBase}/stream?${q.toString()}`);
  } catch {
    return () => {};
  }

  const on = (name: string, fn: (data: Record<string, unknown>) => void) => {
    source.addEventListener(name, (ev) => {
      try {
        fn(JSON.parse((ev as MessageEvent).data) as Record<string, unknown>);
      } catch {
        /* ignore a malformed frame */
      }
    });
  };

  on('agent_message', (d) =>
    h.onAgentMessage({
      id: String(d.id ?? ''),
      body: String(d.body ?? ''),
      agentName: String(d.agentName ?? 'Our team'),
      ts: String(d.ts ?? ''),
      attachment: readAttachment(d.attachment),
    }),
  );
  on('agent_joined', (d) => h.onAgentJoined(String(d.agentName ?? 'Our team')));
  on('agent_left', (d) =>
    h.onAgentLeft(String(d.agentName ?? 'Our team'), d.handedBackToBot !== false),
  );
  on('agent_typing', () => h.onAgentTyping());
  on('auto_message', (d) => h.onAutoMessage(String(d.text ?? '')));
  on('rating_request', () => h.onRatingRequest());
  on('message_deleted', (d) => h.onMessageDeleted(String(d.messageId ?? '')));
  on('no_agent', () => h.onNoAgent());
  on('messages_read', () => h.onMessagesRead());
  on('closed', () => h.onClosed());

  return () => source.close();
}

/** GET /hours → is DTS currently within business hours, and when is a human next
 *  reachable? Fails safe to off-hours (leave-a-message) if the probe can't be
 *  reached; nextOpenText is then null and callers use generic copy. */
export async function fetchHours(apiBase: string): Promise<{ inHours: boolean; nextOpenText: string | null }> {
  try {
    const res = await fetch(apiBase + "/hours");
    if (!res.ok) return { inHours: false, nextOpenText: null };
    const json = await res.json().catch(() => null);
    return {
      inHours: !!(json && json.inHours === true),
      nextOpenText: json && typeof json.nextOpenText === "string" ? json.nextOpenText : null,
    };
  } catch {
    return { inHours: false, nextOpenText: null };
  }
}

function toError(err: unknown): Error {
  return err instanceof Error ? err : new Error(String(err));
}
